import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { ComparesCondition, EqualsCondition } from "./baseCondition.js";

interface PlaybookSectionBaseCondition {
  playbookId: PlaybookId;
  sectionId: string;
}

export type PlaybookSectionApplicabilityCondition =
  PlaybookSectionBaseCondition & EqualsCondition<boolean>;

interface PlaybookYesNoCriterionBaseCondition {
  playbookId: PlaybookId;
  criterionId: string;
  gradingValueType: "yes_no";
}

export type PlaybookYesNoCriterionEqualsCondition =
  PlaybookYesNoCriterionBaseCondition &
    // TODO: Add null to support "not applicable" condition
    EqualsCondition<boolean>;

interface PlaybookScoreCriterionBaseCondition {
  playbookId: PlaybookId;
  criterionId: string;
  gradingValueType: "1_5_score";
}

type PlaybookScoreCriterionValue = 1 | 2 | 3 | 4 | 5;

export type PlaybookScoreCriterionEqualsCondition =
  PlaybookScoreCriterionBaseCondition &
    // TODO: Add null to support "not applicable" condition
    EqualsCondition<PlaybookScoreCriterionValue>;

export type PlaybookScoreCriterionComparesCondition =
  PlaybookScoreCriterionBaseCondition &
    ComparesCondition<PlaybookScoreCriterionValue>;

/**
 * Keeps only gradings whose playbook rubric produced at least one real
 * criterion result (a 1-5 score or a yes/no value)
 */
export interface PlaybookHasResultCondition {
  playbookId: PlaybookId;
  kind: "has_result";
}

export type PlaybookCondition =
  | PlaybookSectionApplicabilityCondition
  | PlaybookYesNoCriterionEqualsCondition
  | PlaybookScoreCriterionEqualsCondition
  | PlaybookScoreCriterionComparesCondition
  | PlaybookHasResultCondition;

export function checkIsPlaybookHasResultCondition(
  condition: PlaybookCondition,
): condition is PlaybookHasResultCondition {
  return "kind" in condition && condition.kind === "has_result";
}

/**
 * Drop the has-result conditions, leaving the criterion/section conditions the
 * user picked. The has-result condition is a page default controlled by
 * "Include calls without grades" rather than something the user added, so
 * comparisons that ask "did the user narrow the results?" run over this.
 */
export function stripPlaybookHasResultConditions(
  conditions: readonly PlaybookCondition[],
): readonly PlaybookCondition[] {
  return conditions.filter(
    (condition) => !checkIsPlaybookHasResultCondition(condition),
  );
}
